/// <reference path="../types/sqljs.d.ts" />
import type { Database } from 'sql.js';
import { getDb, initMockDb } from './sqlite';
import type {
  Auction,
  AuctionBid,
  CreditFacility,
  FacilityRequirement,
  IOU,
  IOUEvent,
  KycCase,
  LenderPosition,
  LendingPool,
  Liquidation,
  MarginAccount,
  MarginAccountCollateral,
  OnchainClaim,
  OnchainClaimReviewCase,
  OnchainId,
  User,
  YieldEntry,
  CfModuleWhitelistEntry,
  CfFacilityModule,
  CfModuleType,
} from './types';

interface SqlRow {
  [key: string]: string | number | null;
}

function queryRows(db: Database, sql: string, params: Array<string | number> = []): SqlRow[] {
  const stmt = db.prepare(sql);
  stmt.bind(params);
  const rows: SqlRow[] = [];
  while (stmt.step()) {
    rows.push(stmt.getAsObject() as SqlRow);
  }
  stmt.free();
  return rows;
}

function toFacility(row: SqlRow): CreditFacility {
  return {
    id: String(row.id),
    name: String(row.name),
    status: row.status as CreditFacility['status'],
    region: String(row.region),
    category: String(row.category),
    settlementTokens: JSON.parse(String(row.settlement_tokens)) as string[],
    collateralTokens: JSON.parse(String(row.collateral_tokens)) as string[],
    interestRatePct: Number(row.interest_rate_pct),
    maxLtvPct: Number(row.max_ltv_pct),
    liquidationThresholdPct: Number(row.liquidation_threshold_pct),
    utilizationPct: Number(row.utilization_pct),
    tvlUsd: Number(row.tvl_usd),
    borrowedUsd: Number(row.borrowed_usd),
    hasDdf: Number(row.has_ddf) === 1,
  };
}

function toUser(row: SqlRow): User {
  return {
    id: String(row.id),
    name: String(row.name),
    wallet: String(row.wallet),
    email: String(row.email),
    investorType: row.investor_type as User['investorType'],
    jurisdiction: String(row.jurisdiction),
    kycStatus: row.kyc_status as User['kycStatus'],
  };
}

function toMarginAccount(row: SqlRow): MarginAccount {
  return {
    id: String(row.id),
    facilityId: String(row.facility_id),
    userId: String(row.user_id),
    wallet: String(row.wallet),
    status: row.status as MarginAccount['status'],
    settlementToken: String(row.settlement_token),
    collateralValueUsd: Number(row.collateral_value_usd),
    borrowedUsd: Number(row.borrowed_usd),
    interestAccrued: Number(row.interest_accrued),
    healthFactor: Number(row.health_factor),
    lastPayment: String(row.last_payment),
    nextPayment: String(row.next_payment),
  };
}

function toCollateral(row: SqlRow): MarginAccountCollateral {
  return {
    id: String(row.id),
    marginAccountId: String(row.margin_account_id),
    tokenSymbol: String(row.token_symbol),
    amount: Number(row.amount),
  };
}

function toKycCase(row: SqlRow): KycCase {
  return {
    id: String(row.id),
    userId: String(row.user_id),
    facilityId: String(row.facility_id),
    provider: String(row.provider),
    submittedDate: String(row.submitted_date),
    riskScore: Number(row.risk_score),
    reviewStatus: row.review_status as KycCase['reviewStatus'],
  };
}

function toLiquidation(row: SqlRow): Liquidation {
  return {
    id: String(row.id),
    facilityId: String(row.facility_id),
    marginAccountId: String(row.margin_account_id),
    wallet: String(row.wallet),
    workflowStatus: row.workflow_status as Liquidation['workflowStatus'],
    debtAmountUsd: Number(row.debt_amount_usd),
    collateralAmountUsd: Number(row.collateral_amount_usd),
    healthFactor: Number(row.health_factor),
    timeInDefault: String(row.time_in_default),
    executedAt: row.executed_at ? String(row.executed_at) : null,
    recoveryRatePct: Number(row.recovery_rate_pct),
  };
}

function toAuction(row: SqlRow): Auction {
  return {
    id: String(row.id),
    facilityId: String(row.facility_id),
    marginAccountId: String(row.margin_account_id),
    title: String(row.title),
    status: row.status as Auction['status'],
    tokenSymbol: String(row.token_symbol),
    tokenName: String(row.token_name),
    collateralAmount: Number(row.collateral_amount),
    collateralValueUsd: Number(row.collateral_value_usd),
    debtAmountUsd: Number(row.debt_amount_usd),
    reservePriceUsd: Number(row.reserve_price_usd),
    currentBidUsd: Number(row.current_bid_usd),
    bidCount: Number(row.bid_count),
    timeRemaining: String(row.time_remaining),
    settlementToken: String(row.settlement_token),
  };
}

function toAuctionBid(row: SqlRow): AuctionBid {
  return {
    id: String(row.id),
    auctionId: String(row.auction_id),
    bidderWallet: String(row.bidder_wallet),
    amountUsd: Number(row.amount_usd),
    timestamp: String(row.timestamp),
    txHash: String(row.tx_hash),
    isLeading: Number(row.is_leading) === 1,
  };
}

function toRequirement(row: SqlRow): FacilityRequirement {
  return {
    id: String(row.id),
    facilityId: String(row.facility_id),
    requirementType: row.requirement_type as FacilityRequirement['requirementType'],
    claimType: String(row.claim_type),
    description: String(row.description),
  };
}

function toOnchainId(row: SqlRow): OnchainId {
  return {
    id: String(row.id),
    userId: String(row.user_id),
    address: String(row.address),
    source: row.source as OnchainId['source'],
  };
}

function toOnchainClaim(row: SqlRow): OnchainClaim {
  return {
    id: String(row.id),
    onchainId: String(row.onchain_id),
    claimType: String(row.claim_type),
    issuer: String(row.issuer),
    value: String(row.value),
    issuedAt: String(row.issued_at),
  };
}

function toLendingPool(row: SqlRow): LendingPool {
  return {
    id: String(row.id),
    facilityId: String(row.facility_id),
    name: String(row.name),
    tvlUsd: Number(row.tvl_usd),
    utilizationPct: Number(row.utilization_pct),
    apyPct: Number(row.apy_pct),
  };
}

function toLenderPosition(row: SqlRow): LenderPosition {
  return {
    id: String(row.id),
    poolId: String(row.pool_id),
    userId: String(row.user_id),
    depositedUsd: Number(row.deposited_usd),
    lpTokens: Number(row.lp_tokens),
    earnedYieldUsd: Number(row.earned_yield_usd),
    withdrawablePct: Number(row.withdrawable_pct),
  };
}

function toIOU(row: SqlRow): IOU {
  return {
    id: String(row.id),
    poolId: String(row.pool_id),
    facilityName: String(row.facility_name),
    originalAmountUsd: Number(row.original_amount_usd),
    repaidAmountUsd: Number(row.repaid_amount_usd),
    remainingAmountUsd: Number(row.remaining_amount_usd),
    status: row.status as IOU['status'],
  };
}

function toIOUEvent(row: SqlRow): IOUEvent {
  return {
    id: String(row.id),
    iouId: String(row.iou_id),
    poolId: String(row.pool_id),
    eventType: row.event_type as IOUEvent['eventType'],
    amountUsd: Number(row.amount_usd),
    createdAt: String(row.created_at),
  };
}

function toYieldEntry(row: SqlRow): YieldEntry {
  return {
    id: String(row.id),
    poolId: String(row.pool_id),
    userId: String(row.user_id),
    weekLabel: String(row.week_label),
    yieldUsd: Number(row.yield_usd),
    cumulativeUsd: Number(row.cumulative_usd),
  };
}

function toOnchainClaimReviewCase(row: SqlRow): OnchainClaimReviewCase {
  return {
    id: String(row.id),
    facilityId: String(row.facility_id),
    userId: String(row.user_id),
    onchainId: String(row.onchain_id),
    claimType: String(row.claim_type),
    provider: String(row.provider),
    issuer: String(row.issuer),
    jurisdiction: String(row.jurisdiction),
    submittedDate: String(row.submitted_date),
    riskScore: Number(row.risk_score),
    reviewStatus: row.review_status as OnchainClaimReviewCase['reviewStatus'],
  };
}

function toModuleEntry(row: SqlRow): CfModuleWhitelistEntry {
  return {
    id: String(row.id),
    moduleType: row.module_type as CfModuleType,
    name: String(row.name),
    description: String(row.description),
    chainAddress: String(row.chain_address),
    chainId: String(row.chain_id),
    updatedAt: String(row.updated_at),
  };
}

export interface MockDataAdapter {
  facilities: {
    list: () => Promise<CreditFacility[]>;
    byId: (id: string) => Promise<CreditFacility | null>;
  };
  users: {
    list: () => Promise<User[]>;
    byId: (id: string) => Promise<User | null>;
  };
  accounts: {
    list: () => Promise<MarginAccount[]>;
    listByFacility: (facilityId: string) => Promise<MarginAccount[]>;
    listByUser: (userId: string) => Promise<MarginAccount[]>;
    byId: (id: string) => Promise<MarginAccount | null>;
    collateralByAccountId: (marginAccountId: string) => Promise<MarginAccountCollateral[]>;
  };
  liquidations: {
    queue: () => Promise<Liquidation[]>;
    history: () => Promise<Liquidation[]>;
    byAccountId: (accountId: string) => Promise<Liquidation[]>;
  };
  auctions: {
    list: () => Promise<Auction[]>;
    byId: (id: string) => Promise<Auction | null>;
    bidsByAuctionId: (auctionId: string) => Promise<AuctionBid[]>;
  };
  kyc: {
    listCases: () => Promise<KycCase[]>;
    byId: (id: string) => Promise<KycCase | null>;
  };
  claimReviews: {
    listOnchainCases: () => Promise<OnchainClaimReviewCase[]>;
    onchainById: (id: string) => Promise<OnchainClaimReviewCase | null>;
  };
  compliance: {
    requirementsByFacility: (facilityId: string) => Promise<FacilityRequirement[]>;
    onchainIdsByUser: (userId: string) => Promise<OnchainId[]>;
    claimsByOnchainId: (onchainId: string) => Promise<OnchainClaim[]>;
    facilityAccessByUser: (userId: string) => Promise<string[]>;
  };
  pools: {
    list: () => Promise<LendingPool[]>;
    byId: (id: string) => Promise<LendingPool | null>;
  };
  lenderPositions: {
    listByUser: (userId: string) => Promise<LenderPosition[]>;
    listByPool: (poolId: string) => Promise<LenderPosition[]>;
    byPoolId: (poolId: string, userId: string) => Promise<LenderPosition | null>;
    countByPool: (poolId: string) => Promise<number>;
  };
  ious: {
    listByPool: (poolId: string) => Promise<IOU[]>;
  };
  iouEvents: {
    listByPool: (poolId: string) => Promise<IOUEvent[]>;
  };
  yieldHistory: {
    listByPool: (poolId: string) => Promise<YieldEntry[]>;
  };
  modules: {
    list: () => Promise<CfModuleWhitelistEntry[]>;
    listByType: (moduleType: CfModuleType) => Promise<CfModuleWhitelistEntry[]>;
    byId: (id: string) => Promise<CfModuleWhitelistEntry | null>;
    create: (entry: Omit<CfModuleWhitelistEntry, 'id' | 'updatedAt'>) => Promise<CfModuleWhitelistEntry>;
    update: (id: string, patch: Partial<Pick<CfModuleWhitelistEntry, 'name' | 'description' | 'chainAddress'>>) => Promise<void>;
    delete: (id: string) => Promise<void>;
    listByFacility: (facilityId: string) => Promise<CfModuleWhitelistEntry[]>;
    listFacilitiesByModule: (moduleId: string) => Promise<Array<{ facilityId: string; facilityName: string }>>;
  };
}

async function withDb<T>(fn: (db: Database) => T): Promise<T> {
  const db = await getDb();
  return fn(db);
}

function createAdapter(): MockDataAdapter {
  return {
    facilities: {
      list: () =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM credit_facilities ORDER BY id ASC').map(toFacility),
        ),
      byId: (id) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM credit_facilities WHERE id = ? LIMIT 1', [id])[0];
          return row ? toFacility(row) : null;
        }),
    },
    users: {
      list: () => withDb((db) => queryRows(db, 'SELECT * FROM users ORDER BY id ASC').map(toUser)),
      byId: (id) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM users WHERE id = ? LIMIT 1', [id])[0];
          return row ? toUser(row) : null;
        }),
    },
    accounts: {
      list: () =>
        withDb((db) => queryRows(db, 'SELECT * FROM margin_accounts ORDER BY id ASC').map(toMarginAccount)),
      listByFacility: (facilityId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM margin_accounts WHERE facility_id = ? ORDER BY id ASC', [facilityId]).map(
            toMarginAccount,
          ),
        ),
      listByUser: (userId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM margin_accounts WHERE user_id = ? ORDER BY id ASC', [userId]).map(
            toMarginAccount,
          ),
        ),
      byId: (id) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM margin_accounts WHERE id = ? LIMIT 1', [id])[0];
          return row ? toMarginAccount(row) : null;
        }),
      collateralByAccountId: (marginAccountId) =>
        withDb((db) =>
          queryRows(
            db,
            'SELECT * FROM margin_account_collateral WHERE margin_account_id = ? ORDER BY token_symbol ASC',
            [marginAccountId],
          ).map(toCollateral),
        ),
    },
    liquidations: {
      queue: () =>
        withDb((db) =>
          queryRows(db, "SELECT * FROM liquidations WHERE workflow_status != 'liquidated' ORDER BY health_factor ASC")
            .map(toLiquidation),
        ),
      history: () =>
        withDb((db) =>
          queryRows(db, "SELECT * FROM liquidations WHERE workflow_status = 'liquidated' ORDER BY executed_at DESC")
            .map(toLiquidation),
        ),
      byAccountId: (accountId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM liquidations WHERE margin_account_id = ? ORDER BY id ASC', [accountId]).map(
            toLiquidation,
          ),
        ),
    },
    auctions: {
      list: () =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM auctions ORDER BY id ASC').map(toAuction),
        ),
      byId: (id) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM auctions WHERE id = ? LIMIT 1', [id])[0];
          return row ? toAuction(row) : null;
        }),
      bidsByAuctionId: (auctionId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM auction_bids WHERE auction_id = ? ORDER BY amount_usd ASC', [auctionId]).map(
            toAuctionBid,
          ),
        ),
    },
    kyc: {
      listCases: () => withDb((db) => queryRows(db, 'SELECT * FROM kyc_cases ORDER BY id ASC').map(toKycCase)),
      byId: (id) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM kyc_cases WHERE id = ? LIMIT 1', [id])[0];
          return row ? toKycCase(row) : null;
        }),
    },
    claimReviews: {
      listOnchainCases: () =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM onchain_claim_review_cases ORDER BY id ASC').map(toOnchainClaimReviewCase),
        ),
      onchainById: (id) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM onchain_claim_review_cases WHERE id = ? LIMIT 1', [id])[0];
          return row ? toOnchainClaimReviewCase(row) : null;
        }),
    },
    compliance: {
      requirementsByFacility: (facilityId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM facility_requirements WHERE facility_id = ? ORDER BY id ASC', [facilityId]).map(
            toRequirement,
          ),
        ),
      onchainIdsByUser: (userId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM onchain_ids WHERE user_id = ? ORDER BY id ASC', [userId]).map(toOnchainId),
        ),
      claimsByOnchainId: (onchainId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM onchain_claims WHERE onchain_id = ? ORDER BY id ASC', [onchainId]).map(
            toOnchainClaim,
          ),
        ),
      facilityAccessByUser: (userId) =>
        withDb((db) =>
          queryRows(db, 'SELECT facility_id FROM user_facility_access WHERE user_id = ? ORDER BY facility_id ASC', [userId]).map(
            (row) => String(row.facility_id),
          ),
        ),
    },
    pools: {
      list: () =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM lending_pools ORDER BY id ASC').map(toLendingPool),
        ),
      byId: (id) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM lending_pools WHERE id = ? LIMIT 1', [id])[0];
          return row ? toLendingPool(row) : null;
        }),
    },
    lenderPositions: {
      listByUser: (userId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM lender_positions WHERE user_id = ? ORDER BY id ASC', [userId]).map(toLenderPosition),
        ),
      listByPool: (poolId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM lender_positions WHERE pool_id = ? ORDER BY deposited_usd DESC', [poolId]).map(toLenderPosition),
        ),
      byPoolId: (poolId, userId) =>
        withDb((db) => {
          const row = queryRows(db, 'SELECT * FROM lender_positions WHERE pool_id = ? AND user_id = ? LIMIT 1', [poolId, userId])[0];
          return row ? toLenderPosition(row) : null;
        }),
      countByPool: (poolId) =>
        withDb((db) => {
          const rows = queryRows(db, 'SELECT COUNT(DISTINCT user_id) as cnt FROM lender_positions WHERE pool_id = ?', [poolId]);
          return Number(rows[0]?.cnt ?? 0);
        }),
    },
    ious: {
      listByPool: (poolId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM ious WHERE pool_id = ? ORDER BY id ASC', [poolId]).map(toIOU),
        ),
    },
    iouEvents: {
      listByPool: (poolId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM iou_events WHERE pool_id = ? ORDER BY created_at DESC', [poolId]).map(toIOUEvent),
        ),
    },
    yieldHistory: {
      listByPool: (poolId) =>
        withDb((db) =>
          queryRows(db, 'SELECT * FROM yield_history WHERE pool_id = ? ORDER BY id ASC', [poolId]).map(toYieldEntry),
        ),
    },
    modules: {
      list: async (): Promise<CfModuleWhitelistEntry[]> => {
        const db = await getDb();
        return queryRows(db, 'SELECT * FROM credit_facility_modules ORDER BY module_type, name').map(toModuleEntry);
      },
      listByType: async (moduleType: CfModuleType): Promise<CfModuleWhitelistEntry[]> => {
        const db = await getDb();
        return queryRows(db, 'SELECT * FROM credit_facility_modules WHERE module_type = ? ORDER BY name', [moduleType]).map(toModuleEntry);
      },
      byId: async (id: string): Promise<CfModuleWhitelistEntry | null> => {
        const db = await getDb();
        const rows = queryRows(db, 'SELECT * FROM credit_facility_modules WHERE id = ?', [id]);
        return rows.length > 0 ? toModuleEntry(rows[0]) : null;
      },
      create: async (entry: Omit<CfModuleWhitelistEntry, 'id' | 'updatedAt'>): Promise<CfModuleWhitelistEntry> => {
        const db = await getDb();
        const id = `mod-${Date.now()}`;
        const updatedAt = new Date().toISOString();
        db.run(
          'INSERT INTO credit_facility_modules (id, module_type, name, description, chain_address, chain_id, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?)',
          [id, entry.moduleType, entry.name, entry.description, entry.chainAddress, entry.chainId, updatedAt],
        );
        return { ...entry, id, updatedAt };
      },
      update: async (id: string, patch: Partial<Pick<CfModuleWhitelistEntry, 'name' | 'description' | 'chainAddress'>>): Promise<void> => {
        const db = await getDb();
        const sets: string[] = [];
        const params: (string | number)[] = [];
        if (patch.name !== undefined) { sets.push('name = ?'); params.push(patch.name); }
        if (patch.description !== undefined) { sets.push('description = ?'); params.push(patch.description); }
        if (patch.chainAddress !== undefined) { sets.push('chain_address = ?'); params.push(patch.chainAddress); }
        sets.push('updated_at = ?');
        params.push(new Date().toISOString());
        params.push(id);
        db.run(`UPDATE credit_facility_modules SET ${sets.join(', ')} WHERE id = ?`, params);
      },
      delete: async (id: string): Promise<void> => {
        const db = await getDb();
        db.run('DELETE FROM credit_facilities_to_modules WHERE module_id = ?', [id]);
        db.run('DELETE FROM credit_facility_modules WHERE id = ?', [id]);
      },
      listByFacility: async (facilityId: string): Promise<CfModuleWhitelistEntry[]> => {
        const db = await getDb();
        return queryRows(
          db,
          `SELECT m.* FROM credit_facility_modules m
           INNER JOIN credit_facilities_to_modules fm ON fm.module_id = m.id
           WHERE fm.facility_id = ?
           ORDER BY m.module_type, m.name`,
          [facilityId],
        ).map(toModuleEntry);
      },
      listFacilitiesByModule: async (moduleId: string): Promise<Array<{ facilityId: string; facilityName: string }>> => {
        const db = await getDb();
        return queryRows(
          db,
          `SELECT cf.id as facility_id, cf.name as facility_name
           FROM credit_facilities cf
           INNER JOIN credit_facilities_to_modules fm ON fm.facility_id = cf.id
           WHERE fm.module_id = ?
           ORDER BY cf.name`,
          [moduleId],
        ).map((row) => ({ facilityId: String(row.facility_id), facilityName: String(row.facility_name) }));
      },
    },
  };
}

let adapterPromise: Promise<MockDataAdapter> | null = null;

export async function getMockDataAdapter(): Promise<MockDataAdapter> {
  if (!adapterPromise) {
    adapterPromise = (async () => {
      await initMockDb();
      return createAdapter();
    })();
  }
  return adapterPromise;
}
